// apps/lk/src/components/ArchetypeHero.tsx
'use client';
import { useState } from 'react';
import Link from 'next/link';
import { ChevronDown, TrendingUp, Briefcase } from 'lucide-react';
import { RadarChart } from '@tb/ui/dashboard';
import { domainInsight } from '@/lib/insights';

type DomainMap = Record<string, { name?: string; value?: number; pct?: number }>;

type Props = {
  archetype?: {
    name?: string;
    shortName?: string;
    description?: string;
    oceanCode?: string;
    jungCode?: string;
    strengths?: string[];
    growthAreas?: string[];
    idealRole?: string;
  } | null;
  archetypeCode?: string | null;
  jungCode?: string | null;
  domains?: DomainMap | null;
  compact?: boolean;
};

export function ArchetypeHero({ archetype, archetypeCode, jungCode, domains, compact }: Props) {
  const [open, setOpen] = useState<string | null>(null);
  const ocean = archetype?.oceanCode ?? archetypeCode;
  const jung = archetype?.jungCode ?? jungCode;
  const name = archetype?.name;
  const desc = archetype?.description;

  if (!ocean && !name) {
    return (
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50 p-6 sm:p-8">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Ваш архетип</p>
        <h2 className="mt-2 font-heading text-xl font-bold text-slate-900">Ещё не определён</h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500">
          Пройдите тест личности — займёт 10 минут. Вы узнаете свой архетип,
          сильные стороны и зоны роста.
        </p>
        <Link href="/tests" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700">
          Пройти тест личности
        </Link>
      </div>
    );
  }

  const hasDomains = domains && Object.keys(domains).length > 0;

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
      {/* ══ Шапка: архетип + коды ══ */}
      <div className="bg-gradient-to-br from-slate-50 to-indigo-50/60 px-6 pt-6 pb-5 sm:px-8 sm:pt-8 sm:pb-7">
        <div className="flex items-center gap-3">
          <span className="font-heading text-2xl font-bold text-slate-300">01</span>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">Ваш архетип</p>
        </div>
        <div className="mt-1.5 flex items-start justify-between gap-4">
          {name && (
            <h2 className="font-heading text-xl font-bold leading-tight text-slate-900 sm:text-2xl">
              {name}
            </h2>
          )}
          {(jung || ocean) && (
            <div className="flex shrink-0 gap-2">
              {jung && (
                <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-mono text-sm font-bold tracking-wide text-slate-700 shadow-sm">
                  {jung}
                </span>
              )}
              {ocean && (
                <span className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1 font-mono text-sm font-bold tracking-wide text-indigo-600 shadow-sm">
                  {ocean}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ══ Радар + бары ══ */}
      {hasDomains && !compact && (
        <div className="grid gap-6 border-b border-slate-100 p-6 sm:p-8 lg:grid-cols-2">
          {/* Радар */}
          <div className="flex items-center justify-center">
            <RadarChart
              points={['N','E','O','A','C'].map(k => ({
                label: domains[k]?.name || k,
                value: domains[k]?.value || 0,
              }))}
              size={260}
            />
          </div>

          {/* Раскрывающиеся бары — единый стиль с «Ваши качества» */}
          <div className="space-y-1.5">
            {(['O','C','E','A','N'] as const).map(k => {
              const d = domains[k];
              if (!d) return null;
              const v = Number(d.pct ?? d.value) || 0;
              const isHigh = v >= 50;
              const insight = domainInsight(k, Number(d.value) || 0, v);
              const isOpen = open === k;

              return (
                <div key={k} className={`rounded-xl border border-l-2 border-slate-200 bg-white ${isHigh ? 'border-l-indigo-400' : 'border-l-slate-300'}`}>
                  <button
                    onClick={() => setOpen(isOpen ? null : k)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 text-sm font-semibold text-slate-900">{d.name || k}</span>
                        <span className="shrink-0 text-xs font-medium text-slate-500">{insight.ofTen}/10 · {insight.word}</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200">
                        <div className={`h-full rounded-full transition-all duration-500 ${isHigh ? 'bg-indigo-500' : 'bg-slate-400'}`} style={{ width: `${Math.max(4, Math.min(100, v))}%` }} />
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} />
                  </button>
                  {isOpen && (
                    <div className="mx-3 mb-3 rounded-xl bg-slate-50 p-4">
                      <p className="text-sm leading-relaxed text-slate-700">{insight.text}</p>
                      {insight.tip && (
                        <p className="mt-2 flex items-start gap-2 text-sm font-medium text-indigo-600">
                          <TrendingUp className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                          {insight.tip}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ Описание ══ */}
      {desc && !compact && (
        <div className="px-6 py-5 sm:px-8">
          <p className="text-[15px] leading-7 text-slate-700">{desc}</p>
        </div>
      )}

      {/* ══ Сильные стороны + зоны роста ══ */}
      {!compact && (archetype?.strengths?.length || archetype?.growthAreas?.length) && (
        <div className="grid gap-px bg-slate-100 sm:grid-cols-2">
          {archetype.strengths && archetype.strengths.length > 0 && (
            <div className="bg-white p-5 sm:p-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-emerald-600">
                Сильные стороны
              </p>
              <ul className="space-y-2">
                {archetype.strengths.map((s, i) => (
                  <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-700">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {archetype.growthAreas && archetype.growthAreas.length > 0 && (
            <div className="bg-white p-5 sm:p-6">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-amber-600">
                Зоны роста
              </p>
              <ul className="space-y-2">
                {archetype.growthAreas.map((s, i) => (
                  <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-700">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-400" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ══ Идеальная роль ══ */}
      {!compact && archetype?.idealRole && (
        <div className="border-t border-slate-100 px-6 py-4 sm:px-8 sm:py-5">
          <div className="flex items-center gap-3">
            <Briefcase className="h-[18px] w-[18px] shrink-0 text-indigo-400" strokeWidth={1.6} />
            <div>
              <p className="text-xs text-slate-400">Вам близко</p>
              <p className="text-sm font-medium text-slate-800">{archetype.idealRole}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
